import type {
  DyspatchEvent,
  ProjectBrief,
  Task,
  TaskAttempt,
  TaskAttemptUsage,
  TaskProgressItem,
} from '@dyspatch/core';
import { Link } from '@tanstack/react-router';
import {
  Activity,
  Bot,
  Briefcase,
  CalendarPlus,
  CheckCircle2,
  CheckSquare,
  Circle,
  CircleDot,
  Coins,
  Eye,
  Flag,
  GitBranch,
  GitFork,
  GitPullRequest,
  Hand,
  Link2,
  Loader,
  Play,
  RotateCcw,
  Timer,
  Undo2,
  Wallet,
} from 'lucide-react';
import { type ComponentType, type ReactNode, useEffect, useState } from 'react';
import { useSession } from '@/components/auth/session';
import { DependencyChips, FailureDetail, PrMark, PropertyGrid } from '@/components/common/bits';
import { Markdown } from '@/components/common/Markdown';
import { taskTone } from '@/components/common/StatusIcon';
import { ActionRow, ActionsPopover } from '@/features/actions/ActionsPopover';
import {
  CancelOnly,
  FailureActions,
  HeldActions,
  ReviewActions,
} from '@/features/actions/GateActions';
import { ChangeTaskActions, PauseActions } from '@/features/actions/PauseActions';
import { useBriefs, useTasks } from '@/hooks/use-fleet';
import { PLAN_KEY } from '@/lib/briefs';
import {
  heldNote,
  holdNeedsAPerson,
  isHeld,
  lastFailureDetail,
  lastWorkerName,
  taskNote,
} from '@/lib/fleet';
import { duration, formatTokens, formatUsd, minutesSince } from '@/lib/format';
import { shortKey } from '@/lib/keys';
import { unmergedDependencies } from '@/lib/merge-order';
import { splitPlanPrompt } from '@/lib/plan-prompt';
import { cn } from '@/lib/utils';

/**
 * The pieces a task is drawn from, shared by its page and by the board's peek.
 *
 * There are two surfaces that show a task: `routes/task.tsx`, the page a person
 * judges it on (issue #61), and `TaskDetail`, the 400px pane the board pops out
 * for a glance. They agree on what a task *is* by importing the same parts from
 * here rather than by two files happening to list the same rows, which is the
 * same rule `Finding` already sets for a finding drawn in two places: one
 * component, or the two readings drift.
 *
 * The split is by what a part answers, not by where it appears:
 *
 *  - `useTaskShape` reads the things a task's facts depend on but does not carry
 *    itself (the job it belongs to, its siblings, who is looking).
 *  - `taskFactRows` is what the task is: state, job, worker, attempts, cost,
 *    edges, branch, pull request. A plain function over the shape, so a test can
 *    assert the page and the pane show the same set. `taskTimingRows` is the
 *    same idea for when each thing happened, and `TaskProperties` draws the two
 *    of them as the one band the page opens with.
 *  - `taskDecision` names the one card that carries a verb, and `TaskDecision`
 *    draws it. `showsTaskVerbs` and `TaskVerbs` do the same for pause, resume
 *    and "change this task".
 *  - `TaskSpec`, `TaskAttempts` and `RunSummary` are regions of the page that
 *    the pane either shows in a different place or does not have room for.
 */

/**
 * Where a task's report remembers whether it was open, for this tab.
 *
 * Per task, because "I opened T-4's report" is a fact about T-4 and not about
 * the pane; per tab, because it is a reading position rather than a preference,
 * and a person who left the console yesterday should get the quiet default back.
 * Private browsing and a storage-disabled browser throw on access rather than
 * returning null, so every touch is guarded and the fallback is simply closed.
 */
function reportKey(taskId: string): string {
  return `dyspatch.report.${taskId}`;
}

function wasOpen(taskId: string): boolean {
  try {
    return sessionStorage.getItem(reportKey(taskId)) === '1';
  } catch {
    return false;
  }
}

function rememberOpen(taskId: string, open: boolean): void {
  try {
    sessionStorage.setItem(reportKey(taskId), open ? '1' : '0');
  } catch {
    // A browser that refuses storage still gets a working disclosure.
  }
}

/**
 * What the agent said about its run, folded away.
 *
 * It is long, it is prose, and it is the second thing you want after the PR link
 * -- so it is a `<details>`: one line at rest, the whole report one click later,
 * and nothing between you and the decision.
 *
 * Two things it used to get wrong. The report is markdown, and it was printed as
 * the characters of markdown - hashes, asterisks and all - for the one piece of
 * prose in the product a machine wrote for a person to read. And whether it was
 * open lived in the DOM, so it was lost whenever the row remounted: a gate list
 * re-sorting under a live stream, the pane moving to another task and back, a
 * reload. On a page that re-sorts itself while you read, that is the report
 * closing under you.
 */
export function RunSummary({ summary, taskId }: { summary?: string | null; taskId: string }) {
  const [open, setOpen] = useState(() => wasOpen(taskId));

  // The pane is one component for many tasks: selecting another one has to bring
  // that task's own state rather than keeping this one's.
  useEffect(() => setOpen(wasOpen(taskId)), [taskId]);

  if (!summary?.trim()) return null;
  return (
    <details
      className="px-gutter pt-2 text-xs"
      open={open}
      onToggle={(event) => {
        const next = event.currentTarget.open;
        setOpen(next);
        rememberOpen(taskId, next);
      }}
    >
      <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
        What the agent reported →
      </summary>
      <Markdown source={summary} className="mt-1.5" />
    </details>
  );
}

/**
 * The eyebrow over a decision card: what this is, and whose move it is.
 *
 * `wait` is the case where the move is nobody's. It draws in the page's own
 * colours and carries no children, because a hold that clears itself has no
 * verb: colouring it like a gate, or offering a button under it, is the reader
 * being told something is wrong when nothing is (issue #12).
 */
function GateBanner({
  label,
  body,
  tone,
  children,
}: {
  label: string;
  body: string;
  tone: 'gate' | 'bad' | 'wait';
  children?: ReactNode;
}) {
  return (
    <section
      className={cn(
        'border-y',
        tone === 'gate' && 'bg-gate-bg/50 border-gate-line/60',
        tone === 'bad' && 'bg-bad-bg/40 border-bad/25',
        tone === 'wait' && 'bg-sunken/60',
      )}
    >
      <div className={cn('px-gutter pt-2.5', children ? undefined : 'pb-2.5')}>
        <div
          className={cn(
            'dy-eyebrow',
            tone === 'gate' && 'text-gate',
            tone === 'bad' && 'text-bad',
            tone === 'wait' && 'text-fg-3',
          )}
        >
          {label}
        </div>
        <p className="text-fg-2 mt-1 text-xs whitespace-pre-wrap">{body}</p>
      </div>
      {children}
    </section>
  );
}

/**
 * The link out of a task, to the brief that asked for it.
 *
 * The title comes from the briefs list the sidebar already holds, so this is a
 * cache read rather than a request. When the brief is not in it - the list is
 * capped, and a very old brief can fall off the end - the row still links,
 * carrying the brief's short key instead of its title: the destination is known
 * from the task itself, and only the label was ever in doubt.
 */
export function BriefLink({
  briefId,
  title,
  className,
}: {
  briefId: string;
  title?: string;
  className?: string;
}) {
  return (
    <Link
      to="/jobs/$briefId"
      params={{ briefId }}
      className={cn('hover:text-gate transition-colors duration-100', className)}
    >
      {title ?? shortKey(briefId)}
    </Link>
  );
}

/**
 * A task's dependency keys as chips, off its own shape rather than the fact
 * table it was first drawn in.
 *
 * `taskFactRows`' `After` row draws through this, and so does the peek panel
 * (issue #100): the panel wants the chips beside its other chips rather than
 * as a table row, and this is the one place the resolve logic - which sibling
 * list a key comes off - lives, so the page and the pane cannot quietly
 * resolve a key two different ways.
 */
export function TaskDependencyChips({ task, shape }: { task: Task; shape: TaskShape }) {
  if (!task.dependsOn.length) return null;
  return (
    <DependencyChips
      keys={task.dependsOn}
      resolve={(key) => {
        const sibling = shape.siblings.find(
          (candidate) => candidate.briefId === task.briefId && candidate.key === key,
        );
        return sibling ? { taskId: sibling.id, title: sibling.title } : undefined;
      }}
    />
  );
}

/**
 * One line of tokens, cost and duration (issue #18). Model only when the
 * caller passes one - a sum across attempts has no single model to name, and
 * `TaskAttemptUsage.model` is already null there for exactly that reason.
 */
export function UsageLine({ usage }: { usage: TaskAttemptUsage }) {
  const tokens = [
    usage.inputTokens != null ? `${formatTokens(usage.inputTokens)} in` : null,
    usage.outputTokens != null ? `${formatTokens(usage.outputTokens)} out` : null,
    usage.cacheCreationTokens ? `${formatTokens(usage.cacheCreationTokens)} cache write` : null,
    usage.cacheReadTokens ? `${formatTokens(usage.cacheReadTokens)} cache read` : null,
  ].filter(Boolean);
  // The rate table (`@dyspatch/core`'s `cost.ts`) does not know every model,
  // and a run priced at the fallback rate is a guess, not a fact - "~$0.40"
  // is honest where "$0.40" would claim more than the hub actually knows,
  // especially since the fallback is Sonnet 5's price and Opus costs 2.5x it.
  const costPrefix = usage.costEstimated ? '~' : '';
  return (
    <span className="text-2xs">
      <span
        className="font-medium"
        title={
          usage.costEstimated
            ? 'Estimate: priced at the fallback rate because the model was not recognised.'
            : undefined
        }
      >
        {costPrefix}
        {formatUsd(usage.costNanoUsd ?? null)}
      </span>
      {tokens.length ? <span className="text-fg-3"> · {tokens.join(', ')}</span> : null}
      {usage.model ? <span className="text-fg-3"> · {usage.model}</span> : null}
      {usage.agentDurationMs != null ? (
        <span className="text-fg-3">
          {' '}
          · {duration(Math.round(usage.agentDurationMs / 60_000))} agent
        </span>
      ) : null}
      {usage.runningDurationMs != null ? (
        <span className="text-fg-3">
          {' '}
          · {duration(Math.round(usage.runningDurationMs / 60_000))} on the worker
        </span>
      ) : null}
    </span>
  );
}

/** One attempt, failures included - the two before a success are the interesting ones (issue #18). */
function AttemptRow({ attempt }: { attempt: TaskAttempt }) {
  const tone =
    attempt.outcome === 'success'
      ? 'text-ok'
      : attempt.outcome === 'limit_hit'
        ? 'text-warn'
        : 'text-bad';
  return (
    <div className="px-gutter flex flex-wrap items-baseline gap-x-2 gap-y-0.5 py-1.5 text-2xs">
      <span className={cn('font-medium', tone)}>attempt {attempt.attempt}</span>
      <span className="text-fg-3">{attempt.outcome.replace('_', ' ')}</span>
      {attempt.workerName ? <span className="text-fg-3">on {attempt.workerName}</span> : null}
      <UsageLine usage={attempt} />
    </div>
  );
}

/** Every attempt, newest first. Nothing at all before the first one ends. */
export function TaskAttempts({
  attempts,
  /**
   * The page draws this inside the same `Surface` as the steps of the run,
   * where a hairline is the two regions genuinely touching; the peek panel is
   * its own single surface with no others beside it, so it takes the region's
   * space from `dy-stack` instead (issue #100).
   */
  bordered = true,
}: {
  attempts: TaskAttempt[];
  bordered?: boolean;
}) {
  if (!attempts.length) return null;
  return (
    <section className={cn(bordered && 'border-t')}>
      <h4 className={cn('dy-eyebrow px-gutter py-1.5', bordered && 'border-b')}>
        Attempts
        <span className="text-fg-3 font-normal"> · newest first, failures included</span>
      </h4>
      <div className="divide-y">
        {attempts.map((attempt) => (
          <AttemptRow key={attempt.id} attempt={attempt} />
        ))}
      </div>
    </section>
  );
}

/**
 * What the task was asked to do, and what it has to hold to be done.
 *
 * The spec is markdown a planning agent wrote for an executing one, and the
 * acceptance criteria are the list a reviewer reads the diff against. Neither
 * had anywhere to be drawn before the task had a page: a 400px pane could not
 * hold a twenty-line spec without becoming a third scroller, so the one thing
 * that says what the task *is* was the one thing the console never showed.
 *
 * What a reviewer sent back is not here, though it reads like part of the spec:
 * it is one of `taskFactRows`' rows, and a page that drew it in both halves
 * would be printing the same paragraph twice.
 *
 * The planning task is the one spec that is folded rather than printed. Its
 * spec is not instructions a planner wrote, it is the prompt the hub built
 * (`splitPlanPrompt`), and most of it - the role preamble, the floor rules and
 * a verbatim JSON output schema - is identical on every job. The only part that
 * differs is the brief, so the brief is what is open at rest and the
 * scaffolding hangs behind one click, as monospace rather than markdown because
 * it is machine input and `Markdown` mangles a raw schema into paragraphs. A
 * PLAN spec somebody rewrote through "Change" no longer parses, and falls back
 * to the plain rendering every other task gets.
 */
export function TaskSpec({ task }: { task: Task }) {
  const prompt = task.key === PLAN_KEY ? splitPlanPrompt(task.spec) : null;
  return (
    <>
      {prompt ? (
        <div className="px-gutter space-y-2 py-3">
          <details className="text-xs">
            <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
              System Prompt →
            </summary>
            <pre className="bg-sunken/60 text-fg-3 mt-1.5 max-h-80 overflow-auto rounded-sm p-2 font-mono whitespace-pre-wrap">
              {prompt.systemPrompt}
            </pre>
          </details>
          <details className="text-xs" open>
            <summary className="text-fg-3 hover:text-fg-2 cursor-pointer list-none select-none transition-colors duration-100">
              User Prompt →
            </summary>
            <Markdown source={prompt.userPrompt} className="mt-1.5 text-sm" />
          </details>
        </div>
      ) : (
        // A measure on the prose, not on the surface under it. A spec is often
        // two thousand words, and a line of two hundred characters is one the
        // eye loses its place in on the way back to the left margin. The card
        // still spans its column, so the criteria and the regions under it keep
        // their full width and only the paragraphs are held to a readable line.
        <Markdown source={task.spec} className="px-gutter max-w-[80ch] py-3 text-sm" />
      )}
      {task.acceptanceCriteria.length ? (
        <div className="border-t px-gutter py-3">
          <div className="dy-eyebrow">Done when</div>
          {/* A checklist, since that is what these are: the list a reviewer
              reads the diff against, one condition per line (issue #64). Not
              an interactive checkbox - nobody here ticks a criterion off by
              hand, a task is done or it isn't - so the icon is the same quiet
              square every row draws, not a control. */}
          <ul className="text-fg-2 mt-1.5 space-y-1.5 text-sm">
            {/* Keyed by position as well as text: nothing stops a planner
                writing the same criterion twice, and this list is read-only,
                never reordered and holds no state of its own. */}
            {task.acceptanceCriteria.map((criterion, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: see above.
              <li key={`${index}-${criterion}`} className="flex items-start gap-2">
                <CheckSquare className="text-fg-3 mt-0.5 size-3.5 shrink-0" aria-hidden />
                <span>{criterion}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-fg-3 border-t px-gutter py-3 text-xs">
          The plan set no acceptance criteria for this task.
        </p>
      )}
    </>
  );
}

/**
 * What a task's facts depend on but does not carry: the job it belongs to, the
 * siblings its edges point at, and who is looking.
 *
 * One hook rather than three reads in each of two surfaces, so the page and the
 * pane cannot disagree about whether a person may pause this task.
 */
export interface TaskShape {
  /** The job this task belongs to, when the (capped) briefs list still holds it. */
  brief?: ProjectBrief;
  /** Every task the console has loaded, for the dependency edges. */
  siblings: Task[];
  /** The dependencies of a task at its gate that are not merged yet (issue #24). */
  waitsOn: { key: string; prUrl: string | null }[];
  /** May this person pause, resume or change the task (docs/JOB-LIFECYCLE.md decision 4). */
  mayShape: boolean;
  isAdmin: boolean;
  isGate: boolean;
  /** Held with nothing left to clear it: a person has to offer it again. */
  stuck: boolean;
  /** Held by the scheduler waiting on the world, which needs no verb. */
  waiting: boolean;
  closed: boolean;
  /** What the last failed attempt printed, from the journal rather than the row. */
  failureDetail: string | null;
  /** Who holds it, or who held it last. */
  worker: string | null;
  /** Minutes this task has been waiting on a human, for a gate. */
  waited: number;
}

export function useTaskShape(task: Task, events: DyspatchEvent[]): TaskShape {
  const { session } = useSession();
  const briefs = useBriefs();
  const tasks = useTasks();

  const isGate = task.status === 'review';
  const brief = briefs.data?.find((candidate) => candidate.id === task.briefId);
  /**
   * Whether this task is safe to merge yet (issue #24). Built off the same
   * capped sibling list `DependsOn` already reads: a dependency the console
   * has not loaded reads as unmerged rather than as clear, which is the safe
   * default when the record is incomplete rather than when the dependency
   * itself is unclear.
   */
  const siblings = tasks.data ?? [];
  const ofThisBrief = siblings.filter((sibling) => sibling.briefId === task.briefId);
  const statusByKey = new Map(ofThisBrief.map((sibling) => [sibling.key, sibling.status]));
  const waitsOn = isGate
    ? unmergedDependencies(task.dependsOn, statusByKey).map((key) => ({
        key,
        prUrl: ofThisBrief.find((sibling) => sibling.key === key)?.prUrl ?? null,
      }))
    : [];
  /**
   * Pause, resume and "change this task" belong to the person who filed the
   * job or an admin, the same set that may approve (docs/JOB-LIFECYCLE.md,
   * decision 4). The hub enforces it; this only decides whether to draw the
   * verbs, so a member is not shown buttons whose only output is a 403.
   */
  const mayShape =
    session.isAdmin ||
    (brief !== undefined &&
      brief.submittedBy.trim().toLowerCase() === session.actor.trim().toLowerCase());
  // One hold needs a verb of its own: the pool is exhausted, nothing is
  // scheduled, and a note left on it reaches nobody until a person offers it
  // again (`holdNeedsAPerson`). The rest are the scheduler waiting on the world
  // and read as such, with the reason and nothing to press.
  const stuck = holdNeedsAPerson(task);

  return {
    brief,
    siblings,
    waitsOn,
    mayShape,
    isAdmin: session.isAdmin,
    isGate,
    stuck,
    waiting: isHeld(task) && !stuck,
    closed: task.status === 'done' || task.status === 'failed' || task.status === 'cancelled',
    // What the last failed attempt printed, to hang under `lastError` in the
    // banner. It comes off the events rather than the task row: the row keeps
    // one reason, the journal keeps the evidence for each attempt, and the
    // newest of those is the one the banner is about.
    failureDetail: lastFailureDetail(events),
    worker: lastWorkerName(task, events),
    waited: minutesSince(task.reviewEnteredAt ?? task.updatedAt),
  };
}

/**
 * One property of a task: what it is called, what it says, and the glyph a
 * reader finds it by.
 *
 * The icon is not decoration. A property band is read by scanning, and a
 * scanner looking for the branch or the pull request finds a shape faster than
 * it finds a word among sixteen other words. It carries no information the
 * label does not already carry, which is what lets `PropertyGrid` hide it from
 * assistive technology.
 */
export type TaskPropertyRow = {
  label: string;
  value: ReactNode;
  icon: ComponentType<{ className?: string }>;
};

/**
 * Everything the task is, as label and value.
 *
 * A plain function rather than a component, so the page and the pane are one
 * list of facts and a test can hold them to it.
 */
export function taskFactRows(task: Task, shape: TaskShape): TaskPropertyRow[] {
  const note = taskNote(task);
  const rows: TaskPropertyRow[] = [
    {
      label: 'State',
      icon: Activity,
      value: (
        // `taskTone` already puts `review` on the gate colour, so this needs no
        // separate `isGate` branch: the tone follows the same map the icon
        // above reads from, and font-medium is what keeps it a step ahead of
        // the plain fg-3 rows underneath it.
        <span className={cn('font-medium', taskTone(task.status))}>
          {task.status}
          {shape.isGate ? ` · ${duration(shape.waited)} waiting` : ''}
        </span>
      ),
    },
  ];
  if (note) rows.push({ label: 'Now', icon: Loader, value: note });
  rows.push({
    label: 'Job',
    icon: Briefcase,
    value: <BriefLink briefId={task.briefId} title={shape.brief?.title} />,
  });
  // "Worker" is a claim about now, so a released task says whose it *was* rather
  // than implying a device still holds it.
  rows.push({
    label: task.assignedWorkerName || !shape.worker ? 'Worker' : 'Last worker',
    icon: Bot,
    value: shape.worker ?? 'none yet',
  });
  rows.push({
    label: 'Attempts',
    icon: RotateCcw,
    value: `${task.attempts} of ${task.maxAttempts}`,
  });
  // The last successful attempt's numbers, not the last attempt outright: a
  // failed retry must not blank out what a reviewer is looking at (issue #18).
  if (task.lastSuccessfulAttemptUsage) {
    rows.push({
      label: 'Last run',
      icon: Coins,
      value: <UsageLine usage={task.lastSuccessfulAttemptUsage} />,
    });
  }
  // Only worth its own row once there is more than one attempt to sum -
  // otherwise it would just repeat the line above.
  if (task.totalUsage && task.attempts > 1) {
    rows.push({
      label: `Total (${task.attempts} attempts)`,
      icon: Wallet,
      value: <UsageLine usage={task.totalUsage} />,
    });
  }
  if (task.dependsOn.length) {
    rows.push({
      label: 'After',
      icon: Link2,
      value: <TaskDependencyChips task={task} shape={shape} />,
    });
  }
  // The branch exists from the run's first minute and outlives a cancel (kept
  // 14 days, docs/JOB-LIFECYCLE.md decision 2), so a cancelled row still names it.
  if (task.branch) {
    rows.push({
      label: 'Branch',
      icon: GitBranch,
      value: <span className="font-mono text-2xs">{task.branch}</span>,
    });
  }
  // What the diff is relative to, when that is not the repo's own base branch.
  // A task that depends on another is cut from that task's branch and its pull
  // request targets it (issue #25), so a reviewer reading this diff is reading
  // an increment on top of a change that is itself still open.
  if (task.baseBranch) {
    rows.push({
      label: 'Built on',
      icon: GitFork,
      value: (
        <span className="text-2xs">
          <span className="font-mono">{task.baseBranch}</span>
          <span className="text-fg-3"> · a task this one depends on, not the base branch</span>
        </span>
      ),
    });
  }
  // The PR link sits on the row while the agent is still running: a draft on
  // GitHub, and said so here, or a reviewer opens half a change as if finished.
  // The branch row above already covers "pushed, no PR yet", so this one only
  // ever has something to add when there is a PR to link. At the gate the
  // decision card carries the link instead, next to the buttons it belongs to.
  if (task.prUrl && !shape.isGate) {
    rows.push({
      label: 'PR',
      icon: GitPullRequest,
      value: <PrMark prUrl={task.prUrl} prDraft={task.prDraft} linked />,
    });
  }
  if (task.reviewFeedback) {
    rows.push({
      label: 'Sent back',
      icon: Undo2,
      value: <span className="whitespace-pre-wrap">{task.reviewFeedback}</span>,
    });
  }
  return rows;
}

/**
 * Every fact and every instant, as the one property band a task page opens
 * with.
 *
 * The facts and the timings were two separate tables before, stacked inside a
 * card in one half of the page, with "Timings" as an eyebrow between them. They
 * are the same kind of thing - short, labelled, scanned rather than read - so
 * they are one band now, and the page spends its width on the two regions that
 * genuinely need it. The order is unchanged: what the task is, then when each
 * thing happened to it.
 */
export function TaskProperties({ task, shape }: { task: Task; shape: TaskShape }) {
  return <PropertyGrid rows={[...taskFactRows(task, shape), ...taskTimingRows(task)]} />;
}

/** Shape over colour, the same rule `StatusIcon` draws a task's own state by. */
const PROGRESS_ITEM_ICON: Record<
  TaskProgressItem['status'],
  ComponentType<{ className?: string }>
> = {
  pending: Circle,
  in_progress: CircleDot,
  completed: CheckCircle2,
};

/**
 * The agent's own todo list, one line per step - what the "Now" row in
 * `taskFactRows` only ever gives a reader the current item of. Drawn only
 * while the task is running, for the reason `taskNote`'s step line is too: a
 * finished task's last reported list is a snapshot from partway through a run
 * whose commits have already squashed into one, and showing it as if it still
 * applied would tell the reader the wrong thing about a task that is over.
 *
 * An old worker's beat carries `{done, total, current}` and no `items` at
 * all - a normal shape, not a broken one - and the fact row above already
 * names the current step, so there is nothing here to draw rather than a
 * shorter, worse copy of the same fact.
 */
export function taskProgressSteps(task: Task): TaskProgressItem[] | null {
  const items = task.status === 'running' ? task.progress?.items : null;
  return items?.length ? items : null;
}

/**
 * `taskProgressSteps`, drawn.
 *
 * The condition above is a function rather than a line inside this component
 * because the task page has to know whether this region has anything in it
 * *before* it decides to draw the heading and the surface around it: a caller
 * that guesses would either print "The run" over an empty card or grow a second
 * copy of the same rule, and a second copy is how the two answers drift.
 */
export function TaskProgressList({
  task,
  /**
   * Same trade-off `TaskAttempts` makes: a hairline inside the page's shared
   * `Surface`, `dy-stack`'s own gap in the peek panel, which has no `Surface`
   * beside this one to draw a line against (issue #100).
   */
  bordered = true,
}: {
  task: Task;
  bordered?: boolean;
}) {
  const items = taskProgressSteps(task);
  if (!items) return null;
  return (
    <section className={cn(bordered && 'border-t')}>
      <h4 className="dy-eyebrow px-gutter pt-2.5">Steps</h4>
      <ul className="space-y-1.5 px-gutter py-2.5 text-sm">
        {items.map((item, index) => {
          const Icon = PROGRESS_ITEM_ICON[item.status];
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: the agent's own list - read-only here, and only ever replaced whole on the next TodoWrite.
            <li key={`${index}-${item.text}`} className="flex items-start gap-2">
              {item.status === 'in_progress' ? (
                <span className="dy-alive text-info relative mt-0.5 inline-flex shrink-0">
                  <Icon className="size-3.5" aria-hidden />
                </span>
              ) : (
                <Icon
                  className={cn(
                    'mt-0.5 size-3.5 shrink-0',
                    item.status === 'completed' ? 'text-ok' : 'text-fg-3',
                  )}
                  aria-hidden
                />
              )}
              <span
                className={cn(
                  item.status === 'completed' && 'text-fg-3 line-through',
                  item.status === 'in_progress' && 'text-foreground font-medium',
                  item.status === 'pending' && 'text-fg-3',
                )}
              >
                {item.text}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** How long ago, with the wall clock behind it for whoever needs the instant. */
function Ago({ iso }: { iso: string }) {
  const minutes = minutesSince(iso);
  return (
    <time dateTime={iso} title={new Date(iso).toLocaleString()}>
      {minutes < 1 ? 'just now' : `${duration(minutes)} ago`}
    </time>
  );
}

/**
 * When each thing happened to this task, for the page.
 *
 * The pane has room for the state and the wait at the gate and nothing more,
 * which is honest at 400px and useless when the question is why a task that was
 * claimed two days ago has not reported. Each row is drawn only if the instant
 * it names exists, so a queued task shows one line rather than five blanks.
 */
export function taskTimingRows(task: Task): TaskPropertyRow[] {
  const rows: TaskPropertyRow[] = [
    { label: 'Planned', icon: CalendarPlus, value: <Ago iso={task.createdAt} /> },
  ];
  if (task.claimedAt) {
    rows.push({ label: 'Claimed', icon: Hand, value: <Ago iso={task.claimedAt} /> });
  }
  if (task.startedAt) {
    rows.push({ label: 'Started', icon: Play, value: <Ago iso={task.startedAt} /> });
  }
  // The wait a reviewer is part of, stamped again each round; `firstReviewEnteredAt`
  // is the one that survives a send-back, and it is the honest answer to "how long
  // has this change been waiting on us".
  if (task.firstReviewEnteredAt) {
    rows.push({ label: 'First review', icon: Eye, value: <Ago iso={task.firstReviewEnteredAt} /> });
  }
  if (task.finishedAt) {
    rows.push({ label: 'Closed', icon: Flag, value: <Ago iso={task.finishedAt} /> });
  }
  rows.push({
    label: 'Timeout',
    icon: Timer,
    value: `${task.timeoutMinutes} minutes an attempt`,
  });
  return rows;
}

/**
 * Which card a task carries, or none.
 *
 * Named apart from the component that draws it because a caller has to know
 * whether there is a card *before* it decides to wrap one: a page that puts an
 * empty sticky div at the top of a flex stack pays for it in a gap nothing
 * fills.
 */
export type TaskDecisionKind = 'gate' | 'failed' | 'stuck' | 'waiting' | 'cancel' | null;

export function taskDecision(task: Task, shape: TaskShape): TaskDecisionKind {
  if (shape.isGate) return 'gate';
  if (task.status === 'failed') return 'failed';
  if (shape.stuck && shape.mayShape) return 'stuck';
  if (shape.waiting) return 'waiting';
  if (shape.isAdmin && (task.status === 'queued' || task.status === 'running')) return 'cancel';
  return null;
}

/** The one card on a task that carries a verb, whichever of the five it is. */
export function TaskDecision({ task, shape }: { task: Task; shape: TaskShape }) {
  const kind = taskDecision(task, shape);
  if (kind === 'gate') {
    return (
      <GateBanner
        label="Your gate · PR review"
        // A run whose last step failed is still in review with its work pushed
        // (issue #120), and the banner may not claim a pull request that is not
        // there; `ReviewActions` draws the branch and the reason underneath.
        body={
          task.prUrl
            ? 'The agent opened a pull request and is waiting on a human decision.'
            : 'The agent pushed its work and is waiting on a human decision, but no pull request was opened.'
        }
        tone="gate"
      >
        <RunSummary summary={task.runSummary} taskId={task.id} />
        <ReviewActions
          taskId={task.id}
          prUrl={task.prUrl}
          branch={task.branch}
          runSummary={task.runSummary}
          waitsOn={shape.waitsOn}
          // What the hub last confirmed with GitHub (issue #125). The task row
          // carries it, so the gate's verbs and its "checked N minutes ago"
          // line come from the same read that drew the page.
          pr={task}
        />
      </GateBanner>
    );
  }
  if (kind === 'failed') {
    return (
      <GateBanner
        label={`Failed · attempt ${task.attempts} of ${task.maxAttempts}`}
        body={task.lastError || 'No error was recorded.'}
        tone="bad"
      >
        {/* The reason is the banner's body; the output that produced it hangs
            under it, closed, so the person deciding what to do next reads one
            paragraph and opens the rest only if the paragraph is not enough. */}
        {shape.failureDetail ? (
          <FailureDetail detail={shape.failureDetail} className="px-gutter pt-2" />
        ) : null}
        <FailureActions taskId={task.id} attempts={task.attempts} maxAttempts={task.maxAttempts} />
      </GateBanner>
    );
  }
  if (kind === 'stuck') {
    return (
      <GateBanner
        label="Held · waiting on a person"
        body={heldNote(task) ?? 'The scheduler is not offering this task to any worker.'}
        tone="gate"
      >
        <HeldActions taskId={task.id} />
      </GateBanner>
    );
  }
  if (kind === 'waiting') {
    return (
      <>
        <GateBanner
          label="Waiting"
          body={
            heldNote(task) ??
            'The scheduler has nowhere to put this task yet. It is offered as soon as it has.'
          }
          tone="wait"
        />
        {/* Cancel is still an admin's verb on anything in flight, and the
            branch below no longer reaches a held task. */}
        {shape.isAdmin ? (
          <div className="border-t">
            <CancelOnly taskId={task.id} />
          </div>
        ) : null}
      </>
    );
  }
  if (kind === 'cancel') {
    return (
      <div className="border-t">
        <CancelOnly taskId={task.id} />
      </div>
    );
  }
  return null;
}

/**
 * Pause and resume, and the one verb that changes a task's instructions
 * (docs/JOB-LIFECYCLE.md 2.5, 2.6). Not at the review gate: that card has its
 * own row, and a send-back is how a reviewed task is changed.
 */
export function showsTaskVerbs(shape: TaskShape): boolean {
  return shape.mayShape && !shape.closed && !shape.isGate;
}

export function TaskVerbs({ task, shape }: { task: Task; shape: TaskShape }) {
  if (!showsTaskVerbs(shape)) return null;
  return (
    <>
      {task.status !== 'draft' ? (
        <div className="border-t">
          <PauseActions task={task} />
        </div>
      ) : null}
      <div className="border-t">
        <ChangeTaskActions task={task} />
      </div>
    </>
  );
}

/**
 * The task's standing verbs, one control in the header (issue #89).
 *
 * `/tasks/:taskId` had the same problem the job page did: "Cancel task" as
 * bare red text in its own bordered band, then Pause in a second, then Change
 * this task in a third, all above the spec - three boxes for the things a
 * person does least often here. This is the one popover that replaces all
 * three, reusing exactly the verbs `taskDecision`'s bare `cancel` kind and
 * `showsTaskVerbs` already decided belong on this task; the gate, failure,
 * held and waiting cards stay pinned under the header the way the job page
 * keeps its own open gate pinned; they already explain themselves and already
 * carry their own verb.
 */
export function TaskVerbsPopover({ task, shape }: { task: Task; shape: TaskShape }) {
  const decision = taskDecision(task, shape);
  const showCancel = decision === 'cancel';
  const showVerbs = showsTaskVerbs(shape);
  if (!showCancel && !showVerbs) return null;

  return (
    <ActionsPopover label="Task actions">
      {showCancel ? (
        <ActionRow sentence="Stops it for good. One already running on a laptop is stopped within a heartbeat.">
          <CancelOnly taskId={task.id} />
        </ActionRow>
      ) : null}
      {showVerbs && task.status !== 'draft' ? (
        <ActionRow sentence="Pausing keeps the work: a running task pushes what it has and stops, and Resume continues it.">
          <PauseActions task={task} />
        </ActionRow>
      ) : null}
      {showVerbs ? (
        <ActionRow>
          <ChangeTaskActions task={task} />
        </ActionRow>
      ) : null}
    </ActionsPopover>
  );
}
